import { useCallback, useMemo, useState } from 'react';
import {
  PublisherReviewStatus,
  UserCommentState,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { log } from '../../loggers/Logger';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';

export interface ReplyViewModel {
  readonly commentId: string;
  readonly author: string;
  readonly body: string;
  readonly relativeTime: string;
  readonly isOptimisticReply: boolean;
}

export interface CommentCardItem {
  readonly commentId: string;
  readonly threadId: string;
  readonly commentBody: string;
  readonly quotedText: string;
  readonly scannerDisplayName: string;
  readonly relativeTime: string;
  readonly isScanner: boolean;
  readonly replies: readonly ReplyViewModel[];
  readonly sectionPath: string;
  readonly startOffset: number;
  readonly principalId: string;
  /** Optional deep-link to the finding's source. Renders a "View source" link when present. */
  readonly sourceUrl: string | undefined;
}

/** Presentation logic for a single comment card: inline editing, mutations, and stage-based permissions. */
export interface CommentCardViewModelResult {
  readonly isEditing: boolean;
  readonly editText: string;
  readonly editError: string | undefined;
  readonly setEditText: (value: string) => void;
  readonly startEdit: () => void;
  readonly saveEdit: () => void;
  readonly cancelEdit: () => void;
  readonly isPending: boolean;
  readonly resolve: () => void;
  readonly markIgnored: () => void;
  readonly reopen: () => void;
  readonly deleteComment: () => void;
  readonly isCanResolve: boolean;
  readonly isCanEdit: boolean;
  readonly isCanDelete: boolean;
  readonly isShowIgnoreAction: boolean;
  readonly resolveLabel: string;
  readonly deleteLabel: string;
  readonly activeStyleVariant: 'ai' | 'default';
}

/** ViewModel for a single comment card. Owns mutations, permission checks, and local editing state. */
export function useCommentCardViewModel(
  comment: CommentCardItem,
  isResolved: boolean,
  draftState: WorkflowState,
  reviewStatus: PublisherReviewStatus,
): CommentCardViewModelResult {
  const manager = useInlineCommentManager();
  const editorService = useContentEditorService();
  const currentUser = useCurrentUser();

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editError, setEditError] = useState<string | undefined>(undefined);
  const [isPending, setIsPending] = useState(false);

  // When a mutation succeeds, flush any staged text edits to keep content and comments in sync.
  const autosaveAfterMutation = useCallback(async () => {
    if (!editorService.isDirty) return;
    try {
      await editorService.flush();
    } catch (err) {
      log.error({ err }, 'Autosave after comment mutation failed');
    }
  }, [editorService]);

  const isQualityCheck = draftState === WorkflowState.QUALITY_CHECK;
  const isPublisherReviewPending =
    draftState === WorkflowState.PUBLISHER_REVIEW && reviewStatus === PublisherReviewStatus.PENDING;
  const isActionableStage = isQualityCheck || isPublisherReviewPending;

  // When the comment is on the resolved tab, only "Reopen" applies.
  const isCanResolve = useMemo(() => {
    if (isResolved) return false;
    if (isQualityCheck) return true;
    if (isPublisherReviewPending) return !comment.isScanner;
    return false;
  }, [isQualityCheck, isPublisherReviewPending, isResolved, comment.isScanner]);

  // When the comment is the current user's human comment, allow editing.
  const isCanEdit = useMemo(() => {
    if (comment.isScanner) return false;
    if (!isActionableStage) return false;
    if (!currentUser.userId) return true;
    return comment.principalId === currentUser.userId;
  }, [isActionableStage, currentUser.userId, comment.isScanner, comment.principalId]);

  // When the comment is the current user's human comment or a scanner comment in publisher-review-pending.
  const isCanDelete = useMemo(() => {
    if (!isActionableStage) return false;
    if (comment.isScanner) return isPublisherReviewPending;
    if (!currentUser.userId) return true;
    return comment.principalId === currentUser.userId;
  }, [
    isActionableStage,
    isPublisherReviewPending,
    currentUser.userId,
    comment.isScanner,
    comment.principalId,
  ]);

  // When the user types in the edit field, clear any prior error.
  const handleSetEditText = useCallback((value: string) => {
    setEditError(undefined);
    setEditText(value);
  }, []);

  const startEdit = useCallback(() => {
    setEditText(comment.commentBody);
    setEditError(undefined);
    setIsEditing(true);
  }, [comment.commentBody]);

  const saveEdit = useCallback(() => {
    if (isPending) return;
    if (editText.trim() && editText.trim() !== comment.commentBody) {
      setIsPending(true);
      setEditError(undefined);
      manager.updateComment(comment.commentId, editText.trim()).then(
        () => {
          setIsEditing(false);
          autosaveAfterMutation().finally(() => setIsPending(false));
        },
        (err) => {
          log.error({ err, commentId: comment.commentId }, 'Failed to edit comment');
          setEditError('Failed to edit comment - please try again');
          setIsPending(false);
        },
      );
    } else {
      setIsEditing(false);
    }
  }, [isPending, editText, comment.commentBody, comment.commentId, manager, autosaveAfterMutation]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  // When a mutation fires, track pending state for this card's loading indicator.
  const runMutation = useCallback(
    (fn: () => Promise<void>, errorMsg: string) => {
      if (isPending) return;
      setIsPending(true);
      fn().then(
        () => autosaveAfterMutation().finally(() => setIsPending(false)),
        (err) => {
          log.error({ err, commentId: comment.commentId }, errorMsg);
          setIsPending(false);
          // The card action has no inline alert slot; surface via the flashbar.
          const detail =
            err instanceof Error
              ? err.message
              : 'An unexpected error occurred. Please report a bug.';
          FlashbarMessageBroker.create().pushErrorMessage(`${errorMsg}: ${detail}`);
        },
      );
    },
    [isPending, autosaveAfterMutation, comment.commentId],
  );

  // Scanner findings resolve to the scan-only IMPLEMENTED state; human comments resolve to RESOLVED.
  // The service rejects IMPLEMENTED for non-scanner comments, so the two paths must not be merged.
  const resolve = useCallback(
    () =>
      runMutation(
        () =>
          comment.isScanner
            ? manager.resolveThreadAs(comment.commentId, UserCommentState.IMPLEMENTED)
            : manager.resolveThread(comment.commentId, true),
        'Failed to resolve comment',
      ),
    [runMutation, manager, comment.commentId, comment.isScanner],
  );

  const markIgnored = useCallback(
    () =>
      runMutation(
        () => manager.resolveThreadAs(comment.commentId, UserCommentState.IGNORED),
        'Failed to mark as ignored',
      ),
    [runMutation, manager, comment.commentId],
  );

  const reopenComment = useCallback(
    () =>
      runMutation(
        () => manager.resolveThread(comment.commentId, false),
        'Failed to reopen comment',
      ),
    [runMutation, manager, comment.commentId],
  );

  const dismissOrDeleteComment = useCallback(
    () =>
      runMutation(
        () =>
          comment.isScanner
            ? manager.resolveThreadAs(comment.commentId, UserCommentState.DELETED)
            : manager.deleteComment(comment.commentId),
        comment.isScanner ? 'Failed to dismiss comment' : 'Failed to delete comment',
      ),
    [runMutation, manager, comment.commentId, comment.isScanner],
  );

  // When the comment is a scanner finding, show the "Ignored" action alongside "Implemented".
  const isShowIgnoreAction = comment.isScanner && isCanResolve;

  // When the comment is from a scanner, label resolution as "Implemented"; human comments use "Resolve".
  const resolveLabel = comment.isScanner ? '✓ Implemented' : '✓ Resolve';

  // When the comment is from a scanner, label removal as "Dismiss"; human comments use "Delete".
  const deleteLabel = comment.isScanner ? '✕ Dismiss' : '✕ Delete';

  // When the comment is from a scanner, the selected card gets an AI-style highlight.
  const activeStyleVariant = comment.isScanner ? ('ai' as const) : ('default' as const);

  return {
    isEditing,
    editText,
    editError,
    setEditText: handleSetEditText,
    startEdit,
    saveEdit,
    cancelEdit,
    isPending,
    resolve,
    markIgnored,
    reopen: reopenComment,
    deleteComment: dismissOrDeleteComment,
    isCanResolve,
    isCanEdit,
    isCanDelete,
    isShowIgnoreAction,
    resolveLabel,
    deleteLabel,
    activeStyleVariant,
  };
}
